import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import API_BASE_URL from "../config";
import PostCard from "../components/posts/PostCard";
import CreatePostModal from "../components/posts/CreatePostModal";
import {
  ArrowLeft, MessageCircle, Plus,
  Clock, TrendingUp, Edit2, X, Check, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const API = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;

const isAdminUser = (user) => {
  if (!user || user === false) return false;
  return (
    user.role === "admin" ||
    user.is_admin === true ||
    user.badges?.includes("admin") ||
    user.type === "admin"
  );
};

export default function ChannelDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const [posts, setPosts] = useState([]);
  const [sort, setSort] = useState("newest");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [places, setPlaces] = useState([]);

  // Edit channel state
  const [showEditChannel, setShowEditChannel] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // Delete state
  const [deleting, setDeleting] = useState(false);



  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [chRes, postsRes, placesRes] = await Promise.all([
        axios.get(`${API}/channels/${id}`, { withCredentials: true }),
        axios.get(`${API}/posts`, { params: { channel_id: id, sort }, withCredentials: true }),
        axios.get(`${API}/places`, { params: { status: "verified", limit: 100 }, withCredentials: true }),
      ]);
      setChannel(chRes.data);
      setPosts(postsRes.data.posts);
      setPlaces(placesRes.data.places);
    } catch (err) { console.error("Failed to load channel details", err); } finally { setLoading(false); }
  }, [id, sort]);

  useEffect(() => { load(); }, [load]);

  const openEditChannel = () => {
    setEditName(channel?.name || "");
    setEditDesc(channel?.description || "");
    setShowEditChannel(true);
  };

  const handleSaveChannel = async () => {
    if (!editName.trim()) { toast.error("Channel name cannot be empty"); return; }
    setEditSaving(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.put(
        `${API}/channels/${id}`,
        { name: editName.trim(), description: editDesc.trim() },
        { withCredentials: true, headers }
      );
      setChannel(data);
      setShowEditChannel(false);
      toast.success("Channel updated!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update channel");
    } finally { setEditSaving(false); }
  };

  const handleDeleteChannel = async () => {
    if (!window.confirm(`Are you sure you want to delete "${channel?.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/channels/${id}`, { withCredentials: true, headers });
      toast.success("Channel deleted!");
      navigate("/channels");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete channel");
    } finally { setDeleting(false); }
  };

  const handleVote = async (postId, value) => {
    if (!user || user === false) { toast.error("Please log in to vote"); return; }
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(`${API}/posts/${postId}/vote`, { value }, { withCredentials: true, headers });
      setPosts((prev) =>
        prev.map((p) => p.id === postId ? { ...p, upvotes: data.upvotes, downvotes: data.downvotes, vote_score: data.vote_score, user_vote: data.user_vote } : p)
      );
    } catch { toast.error("Failed to vote"); }
  };

  const handleSave = async (postId) => {
    if (!user || user === false) { toast.error("Please log in to save"); return; }
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(`${API}/users/posts/${postId}/save`, {}, { withCredentials: true, headers });
      toast.success(data.saved ? "Post saved" : "Post unsaved");
    } catch { toast.error("Failed to save post"); }
  };

  const handleShare = (postId) => {
    const url = `${window.location.origin}/posts/${postId}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied!")).catch(() => toast.error("Failed to copy"));
  };

  if (loading && !channel) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-pulse" data-testid="channel-loading">
        <div className="h-8 bg-muted rounded w-48 mb-4" />
        <div className="h-4 bg-muted rounded w-64 mb-8" />
        <div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-32 bg-muted rounded-2xl" />)}</div>
      </div>
    );
  }

  const admin = isAdminUser(user);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="channel-detail-page">
      <div className="mb-6">
        <Link to="/channels" className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors" data-testid="back-to-channels">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> All Channels
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-2">
            <div>
              <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-foreground">{channel?.name}</h1>
              <p className="text-muted-foreground mt-1">{channel?.description}</p>
            </div>
            {admin && (
              <div className="flex items-center gap-1 mt-1">
                <button
                  onClick={openEditChannel}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                  title="Edit channel"
                  data-testid="edit-channel-btn"
                >
                  <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button
                  onClick={handleDeleteChannel}
                  disabled={deleting}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-60"
                  title="Delete channel"
                  data-testid="delete-channel-btn"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            )}
          </div>
          {user && user !== false && (
            <button onClick={() => setShowCreate(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1.5 self-start" data-testid="create-post-btn">
              <Plus className="w-4 h-4" strokeWidth={1.5} /> New Post
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-1 mb-6 bg-card border border-border rounded-full p-0.5 w-fit">
        <button onClick={() => setSort("newest")} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${sort === "newest" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`} data-testid="sort-newest">
          <Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> Newest
        </button>
        <button onClick={() => setSort("popular")} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${sort === "popular" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`} data-testid="sort-popular">
          <TrendingUp className="w-3.5 h-3.5" strokeWidth={1.5} /> Popular
        </button>
      </div>

      <div className="space-y-4" data-testid="posts-feed">
        {posts.length === 0 ? (
          <div className="bg-card border border-border rounded-2xl p-8 text-center">
            <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-muted-foreground">No posts yet. Start the conversation!</p>
          </div>
        ) : (
          posts.map((post) => (
            <PostCard key={post.id} post={post} onVote={handleVote} onSave={handleSave} onShare={handleShare} />
          ))
        )}
      </div>

      {showCreate && (
        <CreatePostModal
          channelId={id}
          places={places}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {/* Edit Channel Modal */}
      {showEditChannel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-foreground">Edit Channel</h2>
              <button onClick={() => setShowEditChannel(false)} className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted transition-colors">
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Channel Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full border border-border bg-background rounded-xl px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  placeholder="Channel name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Description</label>
                <textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  rows={3}
                  className="w-full border border-border bg-background rounded-xl px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors resize-none"
                  placeholder="Channel description"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowEditChannel(false)} className="flex-1 border border-border text-muted-foreground rounded-xl py-2.5 text-sm font-medium hover:bg-muted transition-colors">
                Cancel
              </button>
              <button
                onClick={handleSaveChannel}
                disabled={editSaving}
                className="flex-1 bg-primary text-primary-foreground rounded-xl py-2.5 text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {editSaving ? "Saving..." : <><Check className="w-4 h-4" strokeWidth={1.5} /> Save Changes</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
